// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ellipsis, mergeWithCss } from "@adl/XDL/Utilities";
import { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import { css } from "styled-components";

type ProductDescriptionVariant = "large" | "medium" | "small";

const commonTextStyles: TypographyPresentationProps = {
    weight: "bold",
    css: css`
        width: 100%;
        letter-spacing: 0;
        ${ellipsis(2)}
    `,
};

const commonLinkStyles: LinkPresentationProps = {
    css: css`
        width: 100%;
        &:focus {
            outline: none;
        }
    `,
};

const lineHeight: { [key in ProductDescriptionVariant]: string } = {
    large: "21px",
    medium: "17px",
    small: "13px",
};

const variantTypesMap: { [key in ProductDescriptionVariant]: LinkPresentationProps } = {
    large: {
        ...mergeWithCss(commonLinkStyles, {
            css: css`
                line-height: ${lineHeight["large"]};
                min-height: 42px;
            `,
        }),
        typographyProps: {
            ...mergeWithCss(commonTextStyles, {
                size: 15,
                css: css`
                    line-height: ${lineHeight["large"]};
                    min-height: 42px;
                `,
            }),
        },
    },
    medium: {
        ...mergeWithCss(commonLinkStyles, {
            css: css`
                line-height: ${lineHeight["medium"]};
                min-height: 34px;
            `,
        }),
        typographyProps: {
            ...mergeWithCss(commonTextStyles, {
                size: 12,
                css: css`
                    line-height: ${lineHeight["medium"]};
                    min-height: 34px;
                `,
            }),
        },
    },
    small: {
        ...mergeWithCss(commonLinkStyles, {
            css: css`
                line-height: ${lineHeight["small"]};
                min-height: 26px;
            `,
        }),
        typographyProps: {
            ...mergeWithCss(commonTextStyles, {
                size: 12,
                css: css`
                    line-height: ${lineHeight["small"]};
                    min-height: 26px;
                `,
            }),
        },
    },
};

const productDescriptionStyles = (
    variant: ProductDescriptionVariant = "medium",
    extendedStyles?: ProductDescriptionStyles,
): ProductDescriptionStyles => {
    return mergeWithCss(
        {
            productDetailLink: { ...variantTypesMap[variant] },
            productDetailDescription: { ...variantTypesMap[variant].typographyProps },
        },
        extendedStyles,
    );
};

export default productDescriptionStyles;
